import React, { useEffect, useMemo, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Users, ArrowLeft, Bell, PlusCircle, History, Trash2, Send } from "lucide-react";
import { useTranslation } from "react-i18next";
import { fetchAuth } from "../common/api";
import { useTheme } from "../common/ThemeContext";
import useAppNotice from "../common/useAppNotice";
import { formatLocalDateTime } from "../common/datetimeUtils";
import {
  MIN_RPE_SESSION_TARGET_PLAYERS,
} from "../overview/rpeOverviewUtils.js";
import {
  applyCoachRpeToSession,
  closeRpeSession,
  deleteRpeSession,
  patchRpeSession,
  saveManualRpeEntries,
} from "../overview/rpeSessionApi.js";
import {
  normalizePlayerPosition,
  PLAYER_POSITIONS,
} from "../common/PlayerPositions.jsx";

const range = Array.from({ length: 10 }, (_, i) => i + 1);

function hasManualValue(value) {
  if (value === "" || value == null) return false;
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric >= 1 && numeric <= 10;
}

const API_ERROR_KEYS = {
  coach_rpe_value_required: "rpe.coach_reference_required",
  invalid_coach_rpe_value: "rpe.coach_reference_required",
  target_player_ids_required: "rpe.min_players_required",
  some_target_player_ids_are_invalid_or_disabled: "rpe.no_enabled_players",
  post_session_survey_required: "rpe.post_session_survey_required_error",
  target_date_must_be_in_past: "rpe.backfill_date_must_be_past",
  invalid_target_date: "rpe.backfill_date_required",
  session_closed: "rpe.error_close_session",
  invalid_session_id: "rpe.error_create_session",
  invalid_entries: "rpe.error_save_manual_entries",
  no_updatable_fields: "rpe.error_update_session",
  error_close_session: "rpe.error_close_session",
  error_delete_session: "rpe.error_delete_session",
  error_quick_send: "rpe.error_quick_send",
  error_update_session: "rpe.error_update_session",
  error_save_manual_entries: "rpe.error_save_manual_entries",
};

/**
 * Multi-session RPE modal.
 * - mode "live": coaches start sessions for today; players respond via mobile.
 * - mode "backfill": coaches create a session on a past sport day and fill values manually.
 */
const RpeSessionModal = ({
  isOpen,
  onClose,
  clubId,
  onOpenSurveySettings,
  mode = "live",
  targetDate = null,
  onSessionCreated,
  onSessionsChanged,
}) => {
  const { isDarkMode } = useTheme();
  const { t, i18n } = useTranslation();
  const { dialog: noticeDialog, alert: showAlert, confirm: showConfirm } = useAppNotice(isDarkMode);
  const isBackfillMode = mode === "backfill";
  const dateLocale = i18n.language?.startsWith("en") ? "en-GB" : "fr-FR";
  const notSetLabel = t("rpe.not_set");

  const translateApiError = useCallback((errorCode, fallbackKey = "rpe.error_generic") => {
    if (!errorCode) return t(fallbackKey);
    if (errorCode === `minimum_${MIN_RPE_SESSION_TARGET_PLAYERS}_players_required`) {
      return t("rpe.min_players_required", { count: MIN_RPE_SESSION_TARGET_PLAYERS });
    }
    const key = API_ERROR_KEYS[errorCode];
    if (key) return t(key, { count: MIN_RPE_SESSION_TARGET_PLAYERS });
    return t("rpe.error_generic", { message: String(errorCode) });
  }, [t]);

  const noticeError = useCallback((message) => showAlert({
    variant: "error",
    title: t("rpe.dialog_error_title"),
    message,
    confirmLabel: t("common.ok"),
  }), [showAlert, t]);

  const noticeWarning = useCallback((titleKey, message) => showAlert({
    variant: "warning",
    title: t(titleKey),
    message,
    confirmLabel: t("common.ok"),
  }), [showAlert, t]);

  const noticeSuccess = useCallback((titleKey, messageKey) => showAlert({
    variant: "success",
    title: t(titleKey),
    message: t(messageKey),
    confirmLabel: t("common.ok"),
  }), [showAlert, t]);

  const confirmAction = useCallback(({
    variant = "warning",
    icon,
    titleKey,
    message,
    highlights,
    confirmLabel,
  }) => showConfirm({
    variant,
    icon,
    title: t(titleKey),
    message,
    highlights,
    confirmLabel: confirmLabel || t("common.confirm"),
    cancelLabel: t("common.cancel"),
  }), [showConfirm, t]);

  const playerHighlights = useCallback((count, value) => [
    { label: t("rpe.dialog_highlight_players"), value: String(count) },
    { label: t("rpe.dialog_highlight_coach_rpe"), value: String(value) },
  ], [t]);

  const missingHighlights = useCallback((count, value) => [
    { label: t("rpe.dialog_highlight_missing"), value: String(count) },
    { label: t("rpe.dialog_highlight_coach_rpe"), value: String(value) },
  ], [t]);

  /* ─────────────────────────── state ─────────────────────────── */
  const [view, setView] = useState("list"); // "list" | "new" | "detail" | "backfill"
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [backfillMeta, setBackfillMeta] = useState(null);

  // roster (loaded on demand for "new" view).
  const [roster, setRoster] = useState([]);
  const [rosterLoaded, setRosterLoaded] = useState(false);
  const [positionFilter, setPositionFilter] = useState("All");

  // "new" form
  const [coachReference, setCoachReference] = useState("");
  const [withHooper, setWithHooper] = useState(false);
  const [label, setLabel] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [starting, setStarting] = useState(false);
  const [startingMode, setStartingMode] = useState(null);
  const [formError, setFormError] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [postSessionSurveyId, setPostSessionSurveyId] = useState(null);

  // "detail" / "backfill" form
  const [manual, setManual] = useState({}); // playerId -> value
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  /* ─────────────────────────── data ─────────────────────────── */
  const refreshSessions = useCallback(async () => {
    if (!clubId) return;
    setLoading(true);
    try {
      const res = await fetchAuth(`/rpe_sessions/${clubId}/active`);
      const data = await res.json();
      if (!res.ok) throw new Error(translateApiError(data.error, "rpe.error_load_sessions"));
      setSessions(Array.isArray(data.sessions) ? data.sessions : []);
    } catch (e) {
      noticeError(e?.message || t("rpe.error_load_sessions"));
    } finally {
      setLoading(false);
    }
  }, [clubId, t, translateApiError, noticeError]);

  const refreshSurveySettings = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const res = await fetchAuth(`/clubs/mobile-stats-settings`);
      const data = await res.json();
      if (!res.ok) throw new Error(translateApiError(data.error, "rpe.error_load_survey_settings"));
      setPostSessionSurveyId(data?.surveyAssignments?.post_session || null);
    } catch {
      setPostSessionSurveyId(null);
    } finally {
      setSettingsLoading(false);
    }
  }, [translateApiError]);

  const loadRoster = useCallback(async () => {
    if (rosterLoaded) return;
    try {
      const res = await fetchAuth(`/players/roster`);
      const data = await res.json();
      if (res.ok) {
        setRoster(Array.isArray(data.players) ? data.players : []);
        setRosterLoaded(true);
      }
    } catch {
      /* ignore */
    }
  }, [rosterLoaded]);

  const resetNewForm = useCallback(() => {
    setCoachReference("");
    setWithHooper(false);
    setLabel("");
    setSelectedIds(new Set());
    setPositionFilter("All");
    setFormError(null);
  }, []);

  const goNew = useCallback(() => {
    resetNewForm();
    refreshSurveySettings();
    loadRoster();
    setView("new");
  }, [resetNewForm, refreshSurveySettings, loadRoster]);

  useEffect(() => {
    if (!isOpen) return;
    setActiveSessionId(null);
    setBackfillMeta(null);
    setManual({});
    refreshSurveySettings();

    if (isBackfillMode) {
      resetNewForm();
      loadRoster();
      setView("new");
      setLoading(false);
      return;
    }

    setView("list");
    refreshSessions();
  }, [isOpen, isBackfillMode, refreshSessions, refreshSurveySettings, resetNewForm, loadRoster]);

  /* ─────────────────────────── helpers ─────────────────────────── */
  const activeSession = useMemo(
    () => sessions.find((s) => s.session_id === activeSessionId) || null,
    [sessions, activeSessionId]
  );
  const hasPostSessionSurvey = Boolean(postSessionSurveyId);

  const filteredRoster = useMemo(() => {
    if (positionFilter === "All") return roster;
    return roster.filter(
      (player) => normalizePlayerPosition(player.position) === positionFilter
    );
  }, [roster, positionFilter]);

  const backfillPlayers = useMemo(() => {
    const ids = backfillMeta?.target_player_ids || [];
    if (!ids.length) return [];
    const idSet = new Set(ids.map((id) => String(id)));
    return roster
      .filter((player) => idSet.has(String(player.id)))
      .sort((a, b) => `${a.last_name || ""} ${a.first_name || ""}`.localeCompare(`${b.last_name || ""} ${b.first_name || ""}`));
  }, [backfillMeta, roster]);

  const canStartSession = selectedIds.size >= MIN_RPE_SESSION_TARGET_PLAYERS;

  const goSurveySettings = () => {
    onClose?.();
    onOpenSurveySettings?.();
  };

  const goDetail = (sid) => {
    setActiveSessionId(sid);
    setManual({});
    setView("detail");
  };

  const toggleSelect = (pid) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid);
      else next.add(pid);
      return next;
    });
  };
  const selectAll = () => setSelectedIds(new Set(filteredRoster.map((p) => p.id)));
  const clearAll = () => setSelectedIds(new Set());

  const startSession = async ({ quickSend = false } = {}) => {
    const coachRef = Number(coachReference);
    if (!Number.isFinite(coachRef) || coachRef < 1 || coachRef > 10) {
      setFormError(t("rpe.coach_reference_required"));
      return;
    }
    if (selectedIds.size < MIN_RPE_SESSION_TARGET_PLAYERS) {
      setFormError(t("rpe.min_players_required", { count: MIN_RPE_SESSION_TARGET_PLAYERS }));
      return;
    }
    if (withHooper && !hasPostSessionSurvey && !quickSend) {
      setFormError(t("rpe.post_session_survey_required_error"));
      return;
    }
    if (isBackfillMode && !targetDate) {
      setFormError(t("rpe.backfill_date_required"));
      return;
    }
    if (quickSend) {
      const confirmed = await confirmAction({
        variant: "send",
        icon: "send",
        titleKey: "rpe.dialog_quick_send_title",
        message: t("rpe.confirm_quick_send_new_body"),
        highlights: playerHighlights(selectedIds.size, coachRef),
        confirmLabel: t("rpe.quick_send"),
      });
      if (!confirmed) return;
    }

    setFormError(null);
    setStarting(true);
    setStartingMode(quickSend ? "quick" : "start");
    try {
      const payload = {
        with_hooper: hasPostSessionSurvey && withHooper && !quickSend,
        coach_rpe_value: coachRef,
        label: label.trim() || null,
        target_player_ids: Array.from(selectedIds),
        quick_send: quickSend,
      };
      if (isBackfillMode) {
        payload.target_date = targetDate;
      }

      const r = await fetchAuth("/rpe_sessions", {
        method: "POST",
        body: JSON.stringify(payload),
        headers: { "Content-Type": "application/json" },
      });
      const d = await r.json();
      if (!r.ok) {
        setFormError(translateApiError(d.error, "rpe.error_create_session"));
        return;
      }

      onSessionsChanged?.({
        type: quickSend ? "quick_send" : "created",
        sessionId: d.session_id,
      });

      if (quickSend) {
        if (isBackfillMode) {
          onSessionCreated?.({
            sessionId: d.session_id,
            targetDate,
          });
          onClose?.();
          return;
        }
        await refreshSessions();
        setView("list");
        return;
      }

      if (isBackfillMode) {
        setActiveSessionId(d.session_id);
        setBackfillMeta({
          session_id: d.session_id,
          coach_rpe_value: coachRef,
          with_hooper: false,
          label: label.trim() || null,
          target_player_ids: Array.from(selectedIds),
          target_date: targetDate,
        });
        setManual({});
        setView("backfill");
        return;
      }

      await refreshSessions();
      setView("list");
    } finally {
      setStarting(false);
      setStartingMode(null);
    }
  };

  const remindMissing = async (sid) => {
    try {
      const res = await fetchAuth(`/rpe_sessions/${sid}/remind`, { method: "POST" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        await noticeError(translateApiError(data.error, "rpe.error_generic"));
        return;
      }
      await noticeSuccess("rpe.reminder_sent_title", "rpe.reminder_sent_body");
    } catch (error) {
      await noticeError(error?.message || t("rpe.error_generic", { message: "" }));
    }
  };

  const closeSession = async (sess) => {
    if (saving || deleting) return;
    const missing = sess.missing_players || [];
    const filled = missing.filter((player) => hasManualValue(manual[player.id]));
    const remaining = missing.length - filled.length;
    const coachRef = Number(sess.coach_rpe_value);
    if (remaining > 0 && !Number.isFinite(coachRef)) {
      await noticeWarning("rpe.dialog_coach_required_title", t("rpe.coach_reference_required"));
      return;
    }
    if (remaining > 0) {
      const confirmed = await confirmAction({
        variant: "warning",
        titleKey: "rpe.dialog_close_title",
        message: t("rpe.confirm_close_partial_body"),
        highlights: missingHighlights(remaining, coachRef),
        confirmLabel: t("rpe.close_session"),
      });
      if (!confirmed) return;
    }

    setSaving(true);
    try {
      if (filled.length) {
        await saveManualRpeEntries(
          sess.session_id,
          filled.map((player) => ({ player_id: player.id, rpe_value: Number(manual[player.id]) }))
        );
      }
      await closeRpeSession(sess.session_id, clubId);
      onSessionsChanged?.({ type: "closed", sessionId: sess.session_id });
      await refreshSessions();
      setView("list");
      setActiveSessionId(null);
    } catch (error) {
      await noticeError(translateApiError(error?.code, "rpe.error_close_session"));
    } finally {
      setSaving(false);
    }
  };

  const quickSendActiveSession = async (sess) => {
    if (saving || deleting) return;
    const coachRef = Number(sess.coach_rpe_value);
    if (!Number.isFinite(coachRef)) {
      await noticeWarning("rpe.dialog_coach_required_title", t("rpe.coach_reference_required"));
      return;
    }
    const missing = sess.missing_players || [];
    if (!missing.length) {
      await closeSession(sess);
      return;
    }
    const confirmed = await confirmAction({
      variant: "send",
      icon: "send",
      titleKey: "rpe.dialog_quick_send_title",
      message: t("rpe.confirm_quick_send_body"),
      highlights: missingHighlights(missing.length, coachRef),
      confirmLabel: t("rpe.quick_send"),
    });
    if (!confirmed) return;

    setSaving(true);
    try {
      const filled = missing.filter((player) => hasManualValue(manual[player.id]));
      if (filled.length) {
        await saveManualRpeEntries(
          sess.session_id,
          filled.map((player) => ({ player_id: player.id, rpe_value: Number(manual[player.id]) }))
        );
      }
      await applyCoachRpeToSession(sess.session_id, { close: true });
      onSessionsChanged?.({ type: "quick_send", sessionId: sess.session_id });
      await refreshSessions();
      setView("list");
      setActiveSessionId(null);
    } catch (error) {
      await noticeError(translateApiError(error?.code, "rpe.error_quick_send"));
    } finally {
      setSaving(false);
    }
  };

  const removeMissingPlayer = async (sess, playerId) => {
    const remaining = (sess.target_player_ids || []).filter((id) => String(id) !== String(playerId));
    if (!remaining.length) {
      await noticeWarning("rpe.dialog_cannot_remove_title", t("rpe.cannot_remove_last_player"));
      return;
    }
    const confirmed = await confirmAction({
      variant: "warning",
      titleKey: "rpe.dialog_remove_player_title",
      message: t("rpe.confirm_remove_player_body"),
      confirmLabel: t("rpe.remove_player"),
    });
    if (!confirmed) return;
    try {
      await patchRpeSession(sess.session_id, { target_player_ids: remaining });
      onSessionsChanged?.({ type: "updated", sessionId: sess.session_id });
      await refreshSessions();
    } catch (error) {
      await noticeError(translateApiError(error?.code, "rpe.error_update_session"));
    }
  };

  const deleteActiveSession = async (sessionId) => {
    if (saving || deleting || !sessionId) return;
    const confirmed = await confirmAction({
      variant: "danger",
      icon: "trash",
      titleKey: "rpe.dialog_delete_title",
      message: t("rpe.confirm_delete_session_body"),
      confirmLabel: t("rpe.delete_session"),
    });
    if (!confirmed) return;
    setDeleting(true);
    try {
      await deleteRpeSession(sessionId);
      onSessionsChanged?.({ type: "deleted", sessionId });
      await refreshSessions();
      setView("list");
      setActiveSessionId(null);
      setBackfillMeta(null);
    } catch (error) {
      await noticeError(translateApiError(error?.code, "rpe.error_delete_session"));
    } finally {
      setDeleting(false);
    }
  };

  const applyCoachToEmptyFields = async (coachValue, players) => {
    const value = Number(coachValue);
    if (!Number.isFinite(value)) {
      await noticeWarning("rpe.dialog_coach_required_title", t("rpe.coach_reference_required"));
      return;
    }
    setManual((prev) => {
      const next = { ...prev };
      players.forEach((player) => {
        if (!hasManualValue(next[player.id])) {
          next[player.id] = value;
        }
      });
      return next;
    });
  };

  const saveBackfillSession = async () => {
    if (!backfillMeta?.session_id || saving || deleting) return;

    const players = backfillPlayers;
    const filled = players.filter((player) => hasManualValue(manual[player.id]));
    const remaining = players.length - filled.length;
    const coachRef = Number(backfillMeta.coach_rpe_value);
    if (remaining > 0 && !Number.isFinite(coachRef)) {
      await noticeWarning("rpe.dialog_coach_required_title", t("rpe.coach_reference_required"));
      return;
    }
    if (remaining > 0) {
      const confirmed = await confirmAction({
        variant: "warning",
        titleKey: "rpe.dialog_close_title",
        message: t("rpe.confirm_close_partial_body"),
        highlights: missingHighlights(remaining, coachRef),
        confirmLabel: t("rpe.save_backfill_session"),
      });
      if (!confirmed) return;
    }

    setSaving(true);
    try {
      if (filled.length) {
        await saveManualRpeEntries(
          backfillMeta.session_id,
          filled.map((player) => ({
            player_id: player.id,
            rpe_value: Number(manual[player.id]),
          })),
          backfillMeta.target_date
        );
      }

      await closeRpeSession(backfillMeta.session_id, clubId, {
        targetDate: backfillMeta.target_date,
      });
      onSessionsChanged?.({ type: "closed", sessionId: backfillMeta.session_id });
      onSessionCreated?.({
        sessionId: backfillMeta.session_id,
        targetDate: backfillMeta.target_date,
      });
      onClose?.();
    } catch (e) {
      await noticeError(e?.message || t("common.error"));
    } finally {
      setSaving(false);
    }
  };

  const quickSendBackfillSession = async () => {
    if (!backfillMeta?.session_id || saving || deleting) return;
    const coachRef = Number(backfillMeta.coach_rpe_value);
    if (!Number.isFinite(coachRef)) {
      await noticeWarning("rpe.dialog_coach_required_title", t("rpe.coach_reference_required"));
      return;
    }
    const confirmed = await confirmAction({
      variant: "send",
      icon: "send",
      titleKey: "rpe.dialog_quick_send_title",
      message: t("rpe.confirm_quick_send_new_body"),
      highlights: playerHighlights(backfillPlayers.length, coachRef),
      confirmLabel: t("rpe.quick_send"),
    });
    if (!confirmed) return;

    setSaving(true);
    try {
      const filled = backfillPlayers.filter((player) => hasManualValue(manual[player.id]));
      if (filled.length) {
        await saveManualRpeEntries(
          backfillMeta.session_id,
          filled.map((player) => ({
            player_id: player.id,
            rpe_value: Number(manual[player.id]),
          })),
          backfillMeta.target_date
        );
      }
      await applyCoachRpeToSession(backfillMeta.session_id, {
        close: true,
        targetDate: backfillMeta.target_date,
      });
      onSessionsChanged?.({ type: "quick_send", sessionId: backfillMeta.session_id });
      onSessionCreated?.({
        sessionId: backfillMeta.session_id,
        targetDate: backfillMeta.target_date,
      });
      onClose?.();
    } catch (e) {
      await noticeError(e?.message || t("rpe.error_quick_send"));
    } finally {
      setSaving(false);
    }
  };

  const discardBackfillIfNeeded = async () => {
    if (view !== "backfill" || !backfillMeta?.session_id) return true;
    const confirmed = await confirmAction({
      variant: "danger",
      icon: "trash",
      titleKey: "rpe.dialog_discard_title",
      message: t("rpe.confirm_discard_backfill_body"),
      confirmLabel: t("rpe.delete_session"),
    });
    if (!confirmed) return false;
    try {
      await deleteRpeSession(backfillMeta.session_id);
      onSessionsChanged?.({ type: "deleted", sessionId: backfillMeta.session_id });
    } catch (error) {
      await noticeError(translateApiError(error?.code, "rpe.error_delete_session"));
      return false;
    }
    setBackfillMeta(null);
    return true;
  };

  const requestClose = async () => {
    const canClose = await discardBackfillIfNeeded();
    if (!canClose) return;
    onClose?.();
  };

  const setValue = (pid, val) => setManual((prev) => ({ ...prev, [pid]: val }));

  const formatBackfillDate = (isoDate) => {
    if (!isoDate) return "";
    const date = new Date(`${isoDate}T12:00:00`);
    return date.toLocaleDateString(dateLocale, {
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  /* ─────────────────────────── render ─────────────────────────── */
  if (!isOpen) return noticeDialog;

  const card = isDarkMode ? "bg-gray-800 text-white" : "bg-white text-gray-900";
  const subtle = isDarkMode ? "text-gray-400" : "text-gray-500";
  const chip = isDarkMode ? "bg-gray-700" : "bg-gray-100";

  const titleByView = {
    list: t("rpe.sessions_title"),
    new: isBackfillMode ? t("rpe.backfill_session_title") : t("rpe.new_session_title"),
    detail: activeSession?.label || t("rpe.session_details"),
    backfill: backfillMeta?.label || t("rpe.backfill_fill_title"),
  };

  const showBackButton = view !== "list" && !(isBackfillMode && view === "new");

  return (
    <>
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <motion.div
        className={`w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl p-6 ${card}`}
        initial={{ scale: 0.92, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
      >
        <div className="flex justify-between items-center mb-5">
          <div className="flex items-center gap-2">
            {showBackButton && (
              <button
                type="button"
                onClick={async () => {
                  if (view === "backfill") {
                    const canLeave = await discardBackfillIfNeeded();
                    if (!canLeave) return;
                    setView("new");
                    return;
                  }
                  setView("list");
                  setActiveSessionId(null);
                }}
                className="p-1 rounded hover:bg-black/10"
                aria-label={t("common.back")}
              >
                <ArrowLeft size={20} />
              </button>
            )}
            <h2 className="text-xl font-semibold">{titleByView[view]}</h2>
          </div>
          <button type="button" onClick={requestClose} aria-label={t("common.close")}><X /></button>
        </div>

        {/* ───────── LIST ───────── */}
        {view === "list" && (
          <>
            {loading && <p>{t("common.loading")}</p>}
            {!loading && sessions.length === 0 && (
              <div className={`p-6 text-center rounded-lg ${chip}`}>
                <p className="mb-3">{t("rpe.no_active_session")}</p>
                <p className={`text-sm ${subtle}`}>{t("rpe.start_session_hint")}</p>
              </div>
            )}

            {!loading && sessions.length > 0 && (
              <div className="space-y-3">
                {sessions.map((s) => {
                  const total = (s.responders?.length || 0) + (s.missing_players?.length || 0);
                  const done = s.responders?.length || 0;
                  return (
                    <div
                      key={s.session_id}
                      className={`flex items-stretch rounded-lg border transition ${
                        isDarkMode ? "border-gray-700 hover:border-gray-500" : "border-gray-200 hover:border-gray-400"
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => goDetail(s.session_id)}
                        className="min-w-0 flex-1 text-left p-4"
                      >
                      <div className="flex justify-between items-start">
                        <div className="min-w-0">
                          <div className="font-semibold truncate">{s.label || t("rpe.session_label")}</div>
                          <div className={`text-xs ${subtle}`}>
                            {formatLocalDateTime(s.created_at)} · {t("feedback.coach_rpe_reference")} {s.coach_rpe_value ?? notSetLabel}
                            {s.with_hooper ? ` · ${t("rpe.daily_feeling_short")}` : ""}
                          </div>
                        </div>
                        <div className={`text-xs rounded-full px-2 py-0.5 ${done === total ? "bg-emerald-600 text-white" : "bg-amber-500 text-white"}`}>
                          {t("rpe.responses_progress", { done, total })}
                        </div>
                      </div>
                      <div className={`mt-2 flex items-center gap-1 text-xs ${subtle}`}>
                        <Users size={12} />
                        <span>
                          {(s.target_player_ids?.length || 0) === 0
                            ? t("rpe.whole_squad")
                            : t("rpe.targeted_players_count", { count: s.target_player_ids.length })}
                        </span>
                      </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteActiveSession(s.session_id)}
                        disabled={deleting}
                        className={`px-3 rounded-r-lg ${isDarkMode ? "text-red-300 hover:bg-red-500/20" : "text-red-600 hover:bg-red-50"}`}
                        aria-label={t("rpe.delete_session")}
                        title={t("rpe.delete_session")}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <button
              type="button"
              onClick={goNew}
              className="mt-5 w-full py-3 rounded-md bg-red-600 hover:bg-red-700 text-white font-semibold flex items-center justify-center gap-2"
            >
              <PlusCircle size={18} /> {t("rpe.start_new_session")}
            </button>
          </>
        )}

        {/* ───────── NEW ───────── */}
        {view === "new" && (
          <>
            {isBackfillMode && targetDate && (
              <div className={`rounded-lg border p-3 mb-4 text-sm ${isDarkMode ? "border-amber-500/40 bg-amber-500/10 text-amber-100" : "border-amber-300 bg-amber-50 text-amber-900"}`}>
                <div className="flex items-center gap-2 font-medium">
                  <History size={16} />
                  {t("rpe.backfill_for_date", { date: formatBackfillDate(targetDate) })}
                </div>
                <p className={`mt-1 text-xs ${isDarkMode ? "text-amber-100/80" : "text-amber-800"}`}>
                  {t("rpe.backfill_hint")}
                </p>
              </div>
            )}

            <div className={`rounded-lg border p-3 mb-4 ${isDarkMode ? "border-gray-700 bg-gray-900/40" : "border-gray-200 bg-gray-50"}`}>
              <label className="block text-sm font-medium mb-1">
                {t("feedback.coach_rpe_reference")} {t("rpe.coach_reference_scale")}
              </label>
              <input
                type="number" min={1} max={10} step={0.5}
                value={coachReference}
                onChange={(e) => setCoachReference(e.target.value)}
                placeholder={t("rpe.coach_reference_placeholder")}
                className={`w-full rounded-md border px-3 py-2 text-sm ${
                  isDarkMode ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-black"
                }`}
              />

              <label className="block text-sm font-medium mt-3 mb-1">
                {t("rpe.label_optional")}
              </label>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder={t("rpe.label_placeholder")}
                maxLength={120}
                className={`w-full rounded-md border px-3 py-2 text-sm ${
                  isDarkMode ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-black"
                }`}
              />

              {!isBackfillMode && (
                <>
                  <label className={`mt-3 flex items-center gap-2 text-sm ${!hasPostSessionSurvey ? "opacity-70" : ""}`}>
                    <input
                      type="checkbox"
                      checked={withHooper}
                      disabled={settingsLoading || !hasPostSessionSurvey}
                      onChange={(e) => setWithHooper(e.target.checked)}
                    />
                    <span>{t("rpe.link_daily_feeling")}</span>
                  </label>
                  {settingsLoading && (
                    <p className={`mt-2 text-xs ${subtle}`}>{t("rpe.checking_post_session_survey")}</p>
                  )}
                  {!settingsLoading && !hasPostSessionSurvey && (
                    <div className={`mt-2 rounded-lg border p-3 text-xs ${isDarkMode ? "border-amber-500/40 bg-amber-500/10 text-amber-100" : "border-amber-300 bg-amber-50 text-amber-800"}`}>
                      <p className="font-medium">{t("rpe.no_post_session_survey_title")}</p>
                      <p className="mt-1">{t("rpe.no_post_session_survey_body")}</p>
                      <button
                        type="button"
                        onClick={goSurveySettings}
                        className={`mt-2 inline-flex rounded-md px-2.5 py-1.5 font-medium ${isDarkMode ? "bg-amber-400 text-gray-950 hover:bg-amber-300" : "bg-amber-600 text-white hover:bg-amber-700"}`}
                      >
                        {t("rpe.create_post_session_survey")}
                      </button>
                    </div>
                  )}
                  {withHooper && (
                    <p className={`mt-2 text-xs ${isDarkMode ? "text-amber-300" : "text-amber-700"}`}>
                      {t("rpe.link_daily_feeling_hint")}
                    </p>
                  )}
                </>
              )}
              {formError && <p className="mt-2 text-xs text-red-500">{formError}</p>}
            </div>

            <div className="flex flex-wrap justify-between items-center gap-2 mb-2">
              <p className="text-sm font-semibold">
                {t("rpe.target_players_label", {
                  selected: selectedIds.size,
                  total: filteredRoster.length,
                  min: MIN_RPE_SESSION_TARGET_PLAYERS,
                })}
              </p>
              <div className="flex flex-wrap gap-2 text-xs items-center">
                <select
                  value={positionFilter}
                  onChange={(e) => setPositionFilter(e.target.value)}
                  className={`rounded border px-2 py-1 text-xs ${
                    isDarkMode ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-black"
                  }`}
                >
                  <option value="All">{t("feedback.all_positions")}</option>
                  {PLAYER_POSITIONS.map((position) => (
                    <option key={position.value} value={position.value}>
                      {t(position.labelKey)}
                    </option>
                  ))}
                </select>
                <button type="button" onClick={selectAll} className="px-2 py-1 rounded bg-emerald-600 text-white">
                  {t("common.select_all")}
                </button>
                <button type="button" onClick={clearAll} className={`px-2 py-1 rounded ${chip}`}>
                  {t("common.clear")}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[40vh] overflow-y-auto p-1">
              {filteredRoster.map((p) => {
                const selected = selectedIds.has(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => toggleSelect(p.id)}
                    className={`relative rounded-lg border-2 p-2 text-left transition ${
                      selected
                        ? "border-emerald-500 bg-emerald-500/10"
                        : isDarkMode ? "border-gray-700" : "border-gray-200"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {p.photo_url ? (
                        <img src={p.photo_url} alt="" className={`w-10 h-10 rounded-full object-cover ${selected ? "ring-2 ring-emerald-500" : ""}`} />
                      ) : (
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${chip}`}>
                          <Users size={16} />
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">
                          {p.first_name} {p.last_name}
                        </div>
                        {p.position && (
                          <div className={`text-[10px] ${subtle} truncate`}>
                            {t(`positions.${String(p.position).toLowerCase()}`, { defaultValue: p.position })}
                          </div>
                        )}
                      </div>
                    </div>
                    <AnimatePresence>
                      {selected && (
                        <motion.div
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          exit={{ scale: 0, opacity: 0 }}
                          className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center text-white"
                        >
                          <Check size={14} />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </button>
                );
              })}
              {filteredRoster.length === 0 && (
                <div className={`col-span-full text-center py-6 ${subtle}`}>
                  {rosterLoaded ? t("rpe.no_enabled_players") : t("common.loading")}
                </div>
              )}
            </div>

            <p className={`mt-2 text-xs ${subtle}`}>
              {t("rpe.min_players_hint", { count: MIN_RPE_SESSION_TARGET_PLAYERS })}
            </p>
            <p className={`mt-1 text-xs ${subtle}`}>{t("rpe.quick_send_new_hint")}</p>

            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => startSession()}
                disabled={starting || !canStartSession}
                className="w-full py-2 rounded-md bg-red-600 hover:bg-red-700 text-white disabled:opacity-60"
              >
              {starting
                ? startingMode === "quick"
                  ? t("rpe.quick_sending")
                  : t("rpe.starting")
                : isBackfillMode
                ? t("rpe.create_and_fill_session")
                : t("rpe.start_session")}
              </button>
              <button
                type="button"
                onClick={() => startSession({ quickSend: true })}
                disabled={starting || !canStartSession}
                className="w-full py-2 rounded-md bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-60 flex items-center justify-center gap-2"
              >
                <Send size={16} />
                {starting && startingMode === "quick" ? t("rpe.quick_sending") : t("rpe.quick_send")}
              </button>
            </div>
          </>
        )}

        {/* ───────── DETAIL (live) ───────── */}
        {view === "detail" && activeSession && (
          <>
            <div className={`rounded-lg border p-3 mb-4 text-sm ${isDarkMode ? "border-gray-700 bg-gray-900/40" : "border-gray-200 bg-gray-50"}`}>
              <p>{t("rpe.opened_at")}: <strong>{formatLocalDateTime(activeSession.created_at)}</strong></p>
              <p>{t("feedback.coach_rpe_reference")}: <strong>{activeSession.coach_rpe_value ?? notSetLabel}</strong></p>
              <p>
                {t("rpe.daily_feeling_linked")}:{" "}
                <strong>{activeSession.with_hooper ? t("common.yes") : t("common.no")}</strong>
              </p>
              <p>
                {t("rpe.responses_status")}: {activeSession.missing_players.length === 0
                  ? t("rpe.everyone_responded")
                  : t("rpe.missing_count", { count: activeSession.missing_players.length })}
              </p>
              <p className={`mt-2 text-xs ${subtle}`}>{t("rpe.close_session_partial_hint")}</p>
            </div>

            {activeSession.missing_players.length > 0 && (
              <>
                <div className={`max-h-64 overflow-y-auto border rounded-md p-2 mb-3 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
                  {activeSession.missing_players.map((p) => (
                    <div key={p.id} className="flex items-center mb-1 gap-2">
                      <span className="w-44 text-sm">{p.first_name} {p.last_name}</span>
                      <select
                        value={manual[p.id] || ""}
                        onChange={(e) => setValue(p.id, Number(e.target.value))}
                        className={`border rounded-md p-2 flex-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          isDarkMode ? "bg-gray-700 text-white border-gray-600" : "bg-white text-black border-gray-300"
                        }`}
                      >
                        <option value="" disabled>{t("rpe.choose_value")}</option>
                        {range.map((v) => <option key={v} value={v}>{v}</option>)}
                      </select>
                      <button
                        type="button"
                        onClick={() => removeMissingPlayer(activeSession, p.id)}
                        className={`p-2 rounded-md ${isDarkMode ? "text-red-300 hover:bg-red-500/20" : "text-red-600 hover:bg-red-50"}`}
                        aria-label={t("rpe.remove_player")}
                        title={t("rpe.remove_player")}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="grid gap-2 sm:grid-cols-2 mb-3">
                  <button
                    type="button"
                    onClick={() => remindMissing(activeSession.session_id)}
                    className="w-full py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center gap-2"
                  >
                    <Bell size={16} /> {t("rpe.send_reminder")}
                  </button>
                  <button
                    type="button"
                    onClick={() => applyCoachToEmptyFields(activeSession.coach_rpe_value, activeSession.missing_players)}
                    className="w-full py-2 rounded-md bg-amber-500 hover:bg-amber-600 text-white flex items-center justify-center gap-2"
                  >
                    <Send size={16} /> {t("rpe.apply_coach_to_empty")}
                  </button>
                </div>
              </>
            )}

            <div className="grid gap-2 sm:grid-cols-2">
              <button
                type="button"
                disabled={saving || deleting}
                onClick={() => closeSession(activeSession)}
                className="w-full py-2 rounded-md bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
              >
                {saving ? t("rpe.closing") : t("rpe.close_session")}
              </button>
              {activeSession.missing_players.length > 0 && Number.isFinite(Number(activeSession.coach_rpe_value)) && (
                <button
                  type="button"
                  disabled={saving || deleting}
                  onClick={() => quickSendActiveSession(activeSession)}
                  className="w-full py-2 rounded-md bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <Send size={16} /> {saving ? t("rpe.quick_sending") : t("rpe.quick_send")}
                </button>
              )}
            </div>
            <button
              type="button"
              disabled={saving || deleting}
              onClick={() => deleteActiveSession(activeSession.session_id)}
              className={`mt-2 w-full py-2 rounded-md flex items-center justify-center gap-2 disabled:opacity-50 ${
                isDarkMode ? "text-red-300 hover:bg-red-500/20" : "text-red-700 hover:bg-red-50"
              }`}
            >
              <Trash2 size={16} />
              {deleting ? t("rpe.deleting_session") : t("rpe.delete_session")}
            </button>
          </>
        )}

        {/* ───────── BACKFILL (past manual entry) ───────── */}
        {view === "backfill" && backfillMeta && (
          <>
            <div className={`rounded-lg border p-3 mb-4 text-sm ${isDarkMode ? "border-gray-700 bg-gray-900/40" : "border-gray-200 bg-gray-50"}`}>
              <p>
                {t("rpe.backfill_date_label")}:{" "}
                <strong>{formatBackfillDate(backfillMeta.target_date)}</strong>
              </p>
              <p>
                {t("feedback.coach_rpe_reference")}:{" "}
                <strong>{backfillMeta.coach_rpe_value ?? notSetLabel}</strong>
              </p>
              <p className={`mt-1 text-xs ${subtle}`}>{t("rpe.backfill_fill_hint")}</p>
            </div>

            <div className={`max-h-72 overflow-y-auto border rounded-md p-2 mb-3 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
              {backfillPlayers.map((p) => (
                <div key={p.id} className="flex items-center mb-2 gap-2">
                  <span className="w-44 shrink-0 text-sm">{p.first_name} {p.last_name}</span>
                  <select
                    value={manual[p.id] || ""}
                    onChange={(e) => setValue(p.id, Number(e.target.value))}
                    className={`border rounded-md p-2 flex-1 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 ${
                      isDarkMode ? "bg-gray-700 text-white border-gray-600" : "bg-white text-black border-gray-300"
                    }`}
                  >
                    <option value="" disabled>{t("rpe.choose_value")}</option>
                    {range.map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
              ))}
            </div>

            <div className="grid gap-2 sm:grid-cols-2 mb-2">
              <button
                type="button"
                onClick={() => applyCoachToEmptyFields(backfillMeta.coach_rpe_value, backfillPlayers)}
                className="w-full py-2 rounded-md bg-amber-500 hover:bg-amber-600 text-white flex items-center justify-center gap-2"
              >
                <Send size={16} /> {t("rpe.apply_coach_to_empty")}
              </button>
              <button
                type="button"
                disabled={saving || deleting}
                onClick={quickSendBackfillSession}
                className="w-full py-2 rounded-md bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Send size={16} /> {saving ? t("rpe.quick_sending") : t("rpe.quick_send")}
              </button>
            </div>
            <button
              type="button"
              disabled={saving || deleting}
              onClick={saveBackfillSession}
              className="w-full py-2 rounded-md bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
            >
              {saving ? t("common.saving") : t("rpe.save_backfill_session")}
            </button>
            <button
              type="button"
              disabled={saving || deleting}
              onClick={() => deleteActiveSession(backfillMeta.session_id)}
              className={`mt-2 w-full py-2 rounded-md flex items-center justify-center gap-2 disabled:opacity-50 ${
                isDarkMode ? "text-red-300 hover:bg-red-500/20" : "text-red-700 hover:bg-red-50"
              }`}
            >
              <Trash2 size={16} />
              {deleting ? t("rpe.deleting_session") : t("rpe.delete_session")}
            </button>
          </>
        )}
      </motion.div>
    </motion.div>
    {noticeDialog}
    </>
  );
};

export default RpeSessionModal;
